import React from "react";
import { Box, Container, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import { FormattedMessage } from "react-intl";
import {
  LayoutDashboard,
  Search,
  BarChart3,
  ClipboardList,
  UserCheck,
} from "lucide-react";
import { useSelector } from "react-redux";

const DashboardShowcase = () => {
  const theme = useTheme();
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const atsFeatures = [
    {
      icon: ClipboardList,
      titleId: "why_choose_us.ats_feature.centralize.title",
      descriptionId: "why_choose_us.ats_feature.centralize.description",
    },
    {
      icon: Search,
      titleId: "why_choose_us.ats_feature.source_profiles.title",
      descriptionId: "why_choose_us.ats_feature.source_profiles.description",
    },
    {
      icon: BarChart3,
      titleId: "why_choose_us.ats_feature.market_trends.title",
      descriptionId: "why_choose_us.ats_feature.market_trends.description",
    },
  ];

  const dashboardTabs = [
    { labelId: "why_choose_us.dashboard.tab.dashboard", active: true },
    { labelId: "why_choose_us.dashboard.tab.application", active: false },
    { labelId: "why_choose_us.dashboard.tab.jobs", active: false },
    { labelId: "why_choose_us.dashboard.tab.cv_library", active: false },
    { labelId: "why_choose_us.dashboard.tab.admin", active: false },
  ];

  const applicationSteps = [
    {
      stageId: "why_choose_us.dashboard.applications.steps.pending.stage",
      descId: "why_choose_us.dashboard.applications.steps.pending.desc",
      count: 10,
      chipBg: "hsl(45 93% 47% / 0.2)",
      chipColor: "hsl(45 93% 62%)",
    },
    {
      stageId: "why_choose_us.dashboard.applications.steps.interview_1.stage",
      descId: "why_choose_us.dashboard.applications.steps.interview_1.desc",
      count: 4,
      chipBg: "hsl(217 91% 60% / 0.2)",
      chipColor: "hsl(217 91% 70%)",
    },
    {
      stageId: "why_choose_us.dashboard.applications.steps.interview_2.stage",
      descId: "why_choose_us.dashboard.applications.steps.interview_2.desc",
      count: 7,
      chipBg: "hsl(153 68% 25% / 0.2)",
      chipColor: "hsl(153 68% 45%)",
    },
    {
      stageId: "why_choose_us.dashboard.applications.steps.technical_tests.stage",
      descId: "why_choose_us.dashboard.applications.steps.technical_tests.desc",
      count: 2,
      chipBg: "hsl(270 91% 65% / 0.2)",
      chipColor: "hsl(270 91% 75%)",
    },
    {
      stageId: "why_choose_us.dashboard.applications.steps.offers.stage",
      descId: "why_choose_us.dashboard.applications.steps.offers.desc",
      count: 1,
      chipBg: "hsl(153 68% 25% / 0.2)",
      chipColor: "hsl(153 68% 45%)",
    },
  ];

  const jobPosts = [
    {
      labelId: "why_choose_us.dashboard.job_posts.status.online",
      count: 4,
      chipBg: "hsl(153 68% 25%)",
      chipColor: theme?.palette.common.white,
    },
    {
      labelId: "why_choose_us.dashboard.job_posts.status.drafts",
      count: 5,
      chipBg: "hsl(45 93% 47% / 0.2)",
      chipColor: "hsl(45 93% 62%)",
    },
    {
      labelId: "why_choose_us.dashboard.job_posts.status.closing_soon",
      count: 3,
      chipBg: "hsl(28 89% 55% / 0.2)",
      chipColor: "hsl(28 89% 65%)",
    },
    {
      labelId: "why_choose_us.dashboard.job_posts.status.closed",
      count: 27,
      chipBg: "hsl(0 0% 100% / 0.1)",
      chipColor: alpha(theme?.palette.common.white, 0.5),
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        pb: { xs: "3rem", md: "5rem" },
        px: { xs: "1rem", sm: "1.5rem", lg: "2rem" },
        bgcolor: "hsl(178 93% 10%)",
        color: theme?.palette.common.white,
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative" }}>
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          sx={{ mt: "5rem" }}
        >
          <Box sx={{ textAlign: "center", mb: "2.5rem" }}>
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                px: "1rem",
                py: "0.375rem",
                borderRadius: "9999px",
                bgcolor: "hsl(72 93% 52% / 0.2)",
                border: "1px solid hsl(72 93% 52% / 0.3)",
                color: "hsl(72 93% 52%)",
                fontSize: "0.75rem",
                fontWeight: 700,
                fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: "1rem",
              }}
            >
              <LayoutDashboard size={14} />
              <FormattedMessage id="why_choose_us.ats.badge" />
            </Box>

            <Box component="h3" sx={{ m: 0, fontSize: { xs: "1.5rem", md: "1.875rem" }, fontWeight: 900, lineHeight: 1.2, fontFamily: "'Segoe UI', 'Work Sans', sans-serif" }}>
              <FormattedMessage id="why_choose_us.ats.heading_prefix" />{" "}
              <Box component="span" sx={{ color: "hsl(153 68% 40%)" }}>
                <FormattedMessage id="why_choose_us.ats.heading_emphasis" />
              </Box>
            </Box>
            <Box component="p" sx={{ mt: "0.75rem", mb: 0, mx: "auto", maxWidth: "38rem", color: alpha(theme?.palette.common.white, 0.5), fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}>
              <FormattedMessage id="why_choose_us.ats.description" />
            </Box>
          </Box>

          <Box
            sx={{
              borderRadius: "1rem",
              border: "1px solid hsl(0 0% 100% / 0.1)",
              bgcolor: "hsl(0 0% 100% / 0.03)",
              backdropFilter: "blur(8px)",
              overflow: "hidden",
              mb: "2rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                borderBottom: "1px solid hsl(0 0% 100% / 0.1)",
                px: { xs: "1rem", md: "1.5rem" },
                overflowX: "auto",
              }}
            >
              {dashboardTabs.map((tab) => (
                <Box
                  key={tab.labelId}
                  sx={{
                    px: "1rem",
                    py: "0.75rem",
                    whiteSpace: "nowrap",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                    color: tab.active
                      ? "hsl(153 68% 40%)"
                      : alpha(theme?.palette.common.white, 0.4),
                    borderBottom: tab.active
                      ? "2px solid hsl(153 68% 25%)"
                      : "2px solid transparent",
                  }}
                >
                  <FormattedMessage id={tab.labelId} />
                </Box>
              ))}
            </Box>

            <Box sx={{ p: { xs: "1.25rem", md: "1.75rem" }, display: "grid", gap: "1rem" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                  gap: "1rem",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: "1rem", borderRadius: "0.75rem", bgcolor: "hsl(0 0% 100% / 0.05)", border: "1px solid hsl(0 0% 100% / 0.1)", p: "1rem" }}>
                  <Box sx={{ width: "2.75rem", height: "2.75rem", borderRadius: "0.5rem", bgcolor: "hsl(153 68% 25% / 0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <UserCheck size={20} color="hsl(153 68% 40%)" />
                  </Box>
                  <Box>
                    <Box component="p" sx={{ m: 0, fontWeight: 700, fontSize: "0.875rem", fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                      <FormattedMessage id="why_choose_us.dashboard.company_name" />
                    </Box>
                    <Box component="p" sx={{ m: 0, fontSize: "0.75rem", color: alpha(theme?.palette.common.white, 0.4), fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}>
                      <FormattedMessage id="why_choose_us.dashboard.company_sector_location" />
                    </Box>
                  </Box>
                  <Box sx={{ ml: "auto", fontSize: "0.625rem", fontWeight: 700, fontFamily: "Segoe UI Semibold, system-ui, sans-serif", px: "0.75rem", py: "0.25rem", borderRadius: "9999px", bgcolor: "hsl(153 68% 25% / 0.15)", color: "hsl(153 68% 40%)" }}>
                    <FormattedMessage id="why_choose_us.dashboard.completion" />
                  </Box>
                </Box>

                <Box sx={{ borderRadius: "0.75rem", bgcolor: "hsl(0 0% 100% / 0.05)", border: "1px solid hsl(0 0% 100% / 0.1)", p: "1rem" }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "0.5rem" }}>
                    <Box component="p" sx={{ m: 0, fontWeight: 700, fontSize: "0.875rem", fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                      <FormattedMessage id="why_choose_us.dashboard.license" />
                    </Box>
                    <Box component="span" sx={{ fontSize: "0.625rem", fontWeight: 700, fontFamily: "Segoe UI Semibold, system-ui, sans-serif", px: "0.75rem", py: "0.25rem", borderRadius: "9999px", bgcolor: "hsl(153 68% 25%)", color: theme?.palette.common.white }}>
                      <FormattedMessage id="why_choose_us.dashboard.upgrade" />
                    </Box>
                  </Box>
                  <Box sx={{ display: "grid", gap: "0.35rem", fontSize: "0.75rem", fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Box component="span" sx={{ color: alpha(theme?.palette.common.white, 0.5), fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}>
                        <FormattedMessage id="why_choose_us.dashboard.metric.jobs" />
                      </Box>
                      <Box component="span" sx={{ fontWeight: 700, fontFamily: "Segoe UI Semibold, system-ui, sans-serif", color: "hsl(153 68% 40%)" }}>9</Box>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Box component="span" sx={{ color: alpha(theme?.palette.common.white, 0.5), fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}>
                        <FormattedMessage id="why_choose_us.dashboard.metric.cv_access" />
                      </Box>
                      <Box component="span" sx={{ fontWeight: 700, fontFamily: "Segoe UI Semibold, system-ui, sans-serif", color: "hsl(153 68% 40%)" }}>
                        <FormattedMessage id="why_choose_us.dashboard.metric.cv_access_value" />
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Box component="span" sx={{ color: alpha(theme?.palette.common.white, 0.5), fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}>
                        <FormattedMessage id="why_choose_us.dashboard.metric.expertise" />
                      </Box>
                      <Box component="span" sx={{ fontWeight: 700, fontFamily: "Segoe UI Semibold, system-ui, sans-serif", color: alpha(theme?.palette.common.white, 0.3) }}>
                        <FormattedMessage id="why_choose_us.dashboard.metric.expertise_value" />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <Box component="span" sx={{ px: "1rem", py: "0.375rem", borderRadius: "9999px", bgcolor: "hsl(153 68% 25%)", color: theme?.palette.common.white, fontSize: "0.6875rem", fontWeight: 700, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                  <FormattedMessage id="why_choose_us.dashboard.pill.overview" />
                </Box>
                <Box component="span" sx={{ px: "1rem", py: "0.375rem", borderRadius: "9999px", bgcolor: "hsl(0 0% 100% / 0.05)", color: alpha(theme?.palette.common.white, 0.4), fontSize: "0.6875rem", fontWeight: 700, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                  <FormattedMessage id="why_choose_us.dashboard.pill.metrics" />
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                  gap: "1rem",
                }}
              >
                <Box
                  sx={{
                    gridColumn: { xs: "span 1", md: "span 2" },
                    borderRadius: "0.75rem",
                    bgcolor: "hsl(0 0% 100% / 0.05)",
                    border: "1px solid hsl(0 0% 100% / 0.1)",
                    p: "1rem",
                  }}
                >
                  <Box component="p" sx={{ m: 0, mb: "0.75rem", fontSize: "0.875rem", fontWeight: 700, color: theme?.palette.common.white, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                    <FormattedMessage id="why_choose_us.dashboard.applications.title" />
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))" },
                      gap: "0.75rem",
                    }}
                  >
                    {applicationSteps.map((step) => (
                      <Box
                        key={step.stageId}
                        sx={{
                          borderRadius: "0.5rem",
                          bgcolor: "hsl(0 0% 100% / 0.05)",
                          border: "1px solid hsl(0 0% 100% / 0.1)",
                          p: "0.75rem",
                        }}
                      >
                        <Box component="p" sx={{ m: 0, mb: "0.25rem", fontSize: "0.6875rem", fontWeight: 700, color: alpha(theme?.palette.common.white, 0.7), fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                          <FormattedMessage id={step.stageId} />
                        </Box>
                        <Box component="p" sx={{ m: 0, mb: "0.5rem", fontSize: "0.5625rem", lineHeight: 1.3, color: alpha(theme?.palette.common.white, 0.3), fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}>
                          <FormattedMessage id={step.descId} />
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Box component="span" sx={{ fontSize: "1.125rem", fontWeight: 900, color: theme?.palette.common.white, fontFamily: "Segoe UI Black, system-ui, sans-serif" }}>
                            {step.count}
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              fontSize: "0.5625rem",
                              fontWeight: 700,
                              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                              px: "0.5rem",
                              py: "0.125rem",
                              borderRadius: "9999px",
                              bgcolor: step.chipBg,
                              color: step.chipColor,
                            }}
                          >
                            →
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>

                <Box
                  sx={{
                    borderRadius: "0.75rem",
                    bgcolor: "hsl(0 0% 100% / 0.05)",
                    border: "1px solid hsl(0 0% 100% / 0.1)",
                    p: "1rem",
                  }}
                >
                  <Box component="p" sx={{ m: 0, mb: "0.75rem", fontSize: "0.875rem", fontWeight: 700, color: theme?.palette.common.white, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                    <FormattedMessage id="why_choose_us.dashboard.job_posts.title" />
                  </Box>
                  <Box sx={{ display: "grid", gap: "0.5rem" }}>
                    {jobPosts.map((item) => (
                      <Box
                        key={item.labelId}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          borderRadius: "0.5rem",
                          bgcolor: "hsl(0 0% 100% / 0.05)",
                          border: "1px solid hsl(0 0% 100% / 0.1)",
                          px: "0.75rem",
                          py: "0.625rem",
                        }}
                      >
                        <Box component="p" sx={{ m: 0, fontSize: "0.6875rem", fontWeight: 700, color: alpha(theme?.palette.common.white, 0.7), fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                          <FormattedMessage id={item.labelId} />
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            fontSize: "0.625rem",
                            fontWeight: 900,
                            fontFamily: "Segoe UI Black, system-ui, sans-serif",
                            px: "0.5rem",
                            py: "0.125rem",
                            borderRadius: "9999px",
                            bgcolor: item.chipBg,
                            color: item.chipColor,
                          }}
                        >
                          {item.count}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
              gap: "1.25rem",
            }}
          >
            {atsFeatures.map((feature, i) => (
              <Box
                key={feature.titleId}
                component={motion.div}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "1rem",
                  p: "1.25rem",
                  borderRadius: "0.75rem",
                  bgcolor: "hsl(0 0% 100% / 0.05)",
                  border: "1px solid hsl(0 0% 100% / 0.1)",
                }}
              >
                <Box sx={{ width: "2.5rem", height: "2.5rem", borderRadius: "0.5rem", bgcolor: "hsl(153 68% 25% / 0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <feature.icon size={18} color="hsl(153 68% 40%)" />
                </Box>
                <Box>
                  <Box component="h4" sx={{ m: 0, mb: "0.25rem", fontSize: "0.95rem", fontWeight: 700, fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}><FormattedMessage id={feature.titleId} /></Box>
                  <Box component="p" sx={{ m: 0, fontSize: "0.8rem", lineHeight: 1.6, color: alpha(theme?.palette.common.white, 0.45), fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}>
                    <FormattedMessage id={feature.descriptionId} />
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          sx={{ textAlign: "center", mt: "3rem" }}
        >
          <Box
            component="a"
            href={`/${i18nLocale}/recruiters/sign_up`}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              px: "2rem",
              py: "0.875rem",
              borderRadius: "0.75rem",
              bgcolor: "hsl(153 68% 25%)",
              color: theme?.palette.common.white,
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 700,
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              transition: "background-color 0.2s ease",
              "&:hover": {
                bgcolor: "hsl(153 68% 25% / 0.9)",
              },
            }}
          >
            <FormattedMessage id="why_choose_us.bottom_cta" />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default DashboardShowcase;
